/**
 * ProjectTrackList Component
 */

import { ListItemAnimations } from '../../../../utils/animation';
import { ITrackItemProps, moveTrack, removeTrack, submitForm } from '../form';
import './project-track-list.scss';

import { capitalize, memoize } from 'lodash';
import * as nextTick from 'next-tick';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect, DispatchProp } from 'react-redux';
import { SortableContainer, SortEndHandler } from 'react-sortable-hoc';
import { FieldsProps, WrappedFieldArrayProps } from 'redux-form';
import { VelocityTransitionGroup } from 'velocity-react';

import { Desktop } from '../../../atoms/mediaQueries';
import { EmptyProjectView } from './EmptyProjectView';
import { IProjectTrackItemProps, ProjectTrackItem } from './ProjectTrackItem';

interface IProjectTrackListProps extends Pick<IProjectTrackItemProps, 'onSaveLicense'> {
  items: FieldsProps<ITrackItemProps>;
  license: ILicenseUsage[];
  project: GQL.IProject;
  onRemoveItem(index: number): () => void;
}

// IMPORTANT: the transform property has to be reset once project track items
// have finished animating in order to ensure the download dropdown menu works
// properly.
function resetAnimTransform(els) {
  els.forEach((el) => {
    if (el.style.transform) {
      el.style.transform = null;
    }
  });
}

export type IProjectTrackListWrapperProps =
  & DispatchProp
  & WrappedFieldArrayProps<ITrackItemProps>
  & Pick<IProjectTrackListProps, 'license' | 'onSaveLicense' | 'project'>;

const classes = new BEM('project-track-list');

export function projectTrackTitle(track: GQL.ITrackSearchResult, version: string | undefined) {
  const versionLabel = version && version !== 'original' ?
    capitalize(version) :
    undefined;

  const title = versionLabel ? `${track.title} (${versionLabel})` : track.title;
  return title;
}

const ProjectTrackList = SortableContainer<IProjectTrackListProps>(({
  items,
  onRemoveItem,
  onSaveLicense,
  license,
  project,
}) => (
  <Desktop>
    {(matches) => {
      const cardHeight = matches ? 128 : 64;
      const TrackItemAnimations = ListItemAnimations(cardHeight);
      const key = matches ? 'for-desktop' : 'for-tablet';
      return (
        <VelocityTransitionGroup
          key={key}
          component="ul"
          {...classes() }
          // tslint:disable-next-line:max-line-length
          enter={{ animation: TrackItemAnimations.In, delay: 500, stagger: 150, height: 'auto', complete: resetAnimTransform }}
          leave={{ animation: TrackItemAnimations.Out }}
          runOnMount
        >
          {items.map((_, index) => {
            const trackItem = items.get(index);
            const edgeResult = !!project.tracks ?
            project.tracks.edges!.find((edge) => edge.id === trackItem.association_id)
              : undefined;
            const trackResult = edgeResult ? edgeResult.node : undefined;

            const title = trackResult && projectTrackTitle(trackResult!, edgeResult && edgeResult.version!);
            return trackResult ? (
              <li key={`${project.id}-${(edgeResult && edgeResult.id)}`} {...classes('item')}>
                <ProjectTrackItem
                  title={title!}
                  cardSize={matches ? 'medium' : 'small'}
                  index={index}
                  license={license}
                  projectId={project.id}
                  remove={onRemoveItem(index)}
                  onSaveLicense={onSaveLicense}
                  trackResult={trackResult}
                  trackEdge={edgeResult}
                  {...trackItem}
                />
              </li>
            ) : null;
          })}
        </VelocityTransitionGroup>
      );
    }}
  </Desktop>
));

class ProjectTrackListWrapper extends React.Component<IProjectTrackListWrapperProps> {
  onRemoveItem = memoize((index: number) => () => {
    this.props.dispatch(removeTrack(index));
    nextTick(() => this.props.dispatch(submitForm()));
  });

  onSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
    this.props.dispatch(moveTrack(oldIndex, newIndex));
    nextTick(() => this.props.dispatch(submitForm()));
  }

  render() {
    const {
      project,
      fields,
      license,
      onSaveLicense,
    } = this.props;

    return fields.length > 0 ? (
      <ProjectTrackList
        key={`tracks-array-${project.id}`}
        project={project}
        distance={10}
        items={fields}
        license={license}
        onRemoveItem={this.onRemoveItem}
        onSaveLicense={onSaveLicense}
        onSortEnd={this.onSortEnd}
        helperClass="is-being-sorted"
      />
    ) : <EmptyProjectView />;
  }
}

const ConnectedProjectTrackListWrapper = connect()(ProjectTrackListWrapper);

export default ConnectedProjectTrackListWrapper;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Projects/projectView/ProjectTrackList.tsx